import { SelectChangeEvent } from '@material-ui/core';
import { debounce } from 'lodash';
import { ChangeEvent, SyntheticEvent, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AnswerItem, Question, QuestionItem } from 'src/@types/generalEnquete';
//一問一答(コンプラ)タブの値を定義する定数オブジェクト取得
import { TAB_VALUE_COMPLIA as TAB_VALUE } from 'src/constants';
import { setGeneralAction5Questions, setGeneralQuestions } from 'src/features/general/enquete-answer/store/enqueteGeneralAnswerSlice';
import { dispatch, useAppSelector } from 'src/redux/store';
import { v1 } from 'uuid';
//DateTimeインポート
import { DateTime } from 'luxon';
//formatインポート
import { format } from 'date-fns';

export const useEnqueteAnswerHandleOnChange = () => {
  // Redux から現在のタブかどうかを取得
  const currentAnswerTab = useAppSelector((state) => state.enqueteAnswer.currentAnswerTab);
  
  // handleOnChangeText related description area
  const handleOnChangeText = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, question: Question, index: number) => {
    debouncehandleChange(event, question, index);
  };

  const debouncehandleChange = useMemo(
    () =>
      debounce((event, question: Question, index: number) => {
        // アンケート期限情報を格納
        const id: string = question.answerItem?.length === 0 ? v1() : (question.answerItem as AnswerItem[])[0].id;

        const answerItem: AnswerItem = {
          id: id,
          label: question.questionItem[0].label,
          other: undefined,
          questionItemId: question.questionItem[0].id,
          value: event.target.value,
        };
        let newQuestion = { ...question };
        newQuestion.answerItem = [answerItem];
        //Questionsを設定する（回答内容追加用）
        currentAnswerTab === TAB_VALUE.QUESTION_ACTION5 
          ? dispatch(setGeneralAction5Questions({ index, value: newQuestion })) 
          :  dispatch(setGeneralQuestions({ index, value: newQuestion }));
      }, 1000),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // handleOnChangeCheckBox related description area
  const handleOnChangeCheckBox = (event: SyntheticEvent, questionItemId: string, index: number, question: Question) => {
    let newQuestion = { ...question };
    if (!newQuestion.answerItem || newQuestion.answerItem.length === 0) {
      const answerItems: AnswerItem[] = [];
      newQuestion.questionItem.forEach((questionItem) => {
        const answerItemData: AnswerItem = {
          id: v1(),
          label: questionItem.label,
          other: undefined,
          questionItemId: questionItem.id,
          value: false,
        };
        answerItems.push(answerItemData);
      });
      newQuestion.answerItem = answerItems;
    }

    const newAnswerItem: AnswerItem[] = newQuestion.answerItem as AnswerItem[];
    newQuestion.answerItem = [];
    const answerItems: AnswerItem[] = [];
    newAnswerItem.forEach((answerItem) => {
      if (questionItemId === answerItem.questionItemId) {
        const answerItemData: AnswerItem = {
          id: answerItem.id,
          label: answerItem.label,
          other: answerItem.other,
          questionItemId: answerItem.questionItemId,
          value: (event.target as HTMLInputElement).checked,
        };
        answerItems.push(answerItemData);
      } else {
        answerItems.push(answerItem);
      }
    });
    newQuestion.answerItem = answerItems;
    //Questionsを設定する（回答内容追加用）
    currentAnswerTab === TAB_VALUE.QUESTION_ACTION5 
      ? dispatch(setGeneralAction5Questions({ index, value: newQuestion })) 
      :  dispatch(setGeneralQuestions({ index, value: newQuestion }));
  };

  // Get default value (element specific)
  const getDefaultValue = (answerItems: AnswerItem[], questionItems: QuestionItem[]): string | undefined => {
    let defaultValue: string = '';
    let index = 0;
    answerItems.forEach((answerItem) => {
      if (answerItem.value === true) {
        defaultValue = questionItems[index].id;
        return defaultValue;
      }
      index++;
    });
    return defaultValue;
  };

  // Get default value (dropdown, radio button)
  const dropdownAndRadioDefaultValue = (index: number, question: Question, methods?: UseFormReturn): string | undefined => {
    if (methods && (question.answerItem as AnswerItem[]) && (question.answerItem as AnswerItem[]).length !== 0) {
      return getDefaultValue(question.answerItem as AnswerItem[], question.questionItem as QuestionItem[]);
    } else {
      return '';
    }
  };

  // HandleOnChange processing (dropdown, radio button)
  const handleOnChangeDropdownAndRadio = (event: SelectChangeEvent, index: number, question: Question) => {
    let newQuestion = { ...question };
    if (!newQuestion.answerItem || newQuestion.answerItem.length === 0) {
      const answerItems: AnswerItem[] = [];
      newQuestion.questionItem.forEach((questionItem) => {
        const answerItemData: AnswerItem = {
          id: v1(),
          label: questionItem.label,
          other: undefined,
          questionItemId: questionItem.id,
          value: false,
        };
        answerItems.push(answerItemData);
      });
      newQuestion.answerItem = answerItems;
    }

    let newAnswerItem: AnswerItem[] = newQuestion.answerItem as AnswerItem[];
    newQuestion.answerItem = [];
    const answerItems: AnswerItem[] = [];
    newAnswerItem.forEach((answerItem) => {
      const answerItemData: AnswerItem = {
        id: answerItem.id,
        label: answerItem.label,
        other: answerItem.other,
        questionItemId: answerItem.questionItemId,
        value: answerItem.questionItemId === event.target.value ? true : false,
      };
      answerItems.push(answerItemData);
    });
    newQuestion.answerItem = answerItems;
    //Questionsを設定する（回答内容追加用）
    currentAnswerTab === TAB_VALUE.QUESTION_ACTION5 
      ? dispatch(setGeneralAction5Questions({ index, value: newQuestion })) 
      :  dispatch(setGeneralQuestions({ index, value: newQuestion }));
  };

  /**
   * 日付変更時の変更イベント
   * @param e 変更値
   * @param question 現在回答の質問
   * @param index 現在の質問のインデックス
   * @param answerIndex 回答インデックス
   */
  const handleOnChangeDate = (e: any, question: Question, index: number, answerIndex: number) =>{
    let newQuestion = { ...question };
    //回答情報が存在しない場合、作成する。
    if(!newQuestion.answerItem || newQuestion.answerItem.length === 0){
      const answerItems: AnswerItem[] = [];
      newQuestion.questionItem.forEach((questionItem) => {
        const answerItemData: AnswerItem = {
          // ID(uid)
          id: v1(),
          //ラベル
          label: questionItem.label,
          other: undefined,
          //QuestionItemとの紐づけ用ID
          questionItemId: questionItem.id,
          //回答内容を格納
          value:  '',
        };
        answerItems.push(answerItemData);
      });
      newQuestion.answerItem = answerItems;
    }
    //入力値の日付判定を行い、正しい日付でない場合は''を設定する
    const setDate = e instanceof Date && !isNaN(new Date(e).getTime())
    ? format(new Date(e), 'yyyy-MM-dd HH:mm:ss.SSSxxx')
    : !isNaN(new Date(e).getTime())  &&  DateTime.fromFormat(e ?? '', 'yyyy-MM-dd').isValid 
    ? format(new Date(e), 'yyyy-MM-dd HH:mm:ss.SSSxxx')
    : '';
    //現在の選択値を設定する
    let newAnswerItem: AnswerItem[] = newQuestion.answerItem as AnswerItem[];
      newQuestion.answerItem = [];
      const answerItems: AnswerItem[] = [];
      newAnswerItem.forEach((answerItem, idx) => {
        const answerItemData: AnswerItem = {
          // ID(uid)
          id: answerItem.id,
          //ラベル
          label: answerItem.label,
          other: answerItem.other,
          //QuestionItemとの紐づけ用ID
          questionItemId: answerItem.questionItemId,
          //回答内容を格納
          value: answerIndex === idx ? setDate : answerItem.value,
        };
      answerItems.push(answerItemData);
    });
    newQuestion.answerItem = answerItems;

    //Questionsを設定する（回答内容追加用）
    currentAnswerTab === TAB_VALUE.QUESTION_ACTION5 
      ? dispatch(setGeneralAction5Questions({ index, value: newQuestion })) 
      :  dispatch(setGeneralQuestions({ index, value: newQuestion }));
  }
  return {
    handleOnChangeText,
    handleOnChangeCheckBox,
    handleOnChangeDropdownAndRadio,
    dropdownAndRadioDefaultValue,
    handleOnChangeDate,
  };
};
